import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import apiClient from '../service/ApiClient';
import doorLockService from '../service/DoorLockService';

/**
 * Door-lock bridge status + cards waiting for the encoder.
 *
 * Status is polled every 60s; while a bridge is configured the
 * "needs attention" list is polled every 15s so a self check-in that
 * is waiting for a card shows up at the front desk quickly.
 */
const DoorLockContext = createContext({
  status: null,
  configured: false,
  attentionJobs: [],
  refresh: () => {},
});

const STATUS_POLL_MS = 60 * 1000;
const ATTENTION_POLL_MS = 15 * 1000;

export function DoorLockProvider({ children }) {
  const [status, setStatus] = useState(null);
  const [attentionJobs, setAttentionJobs] = useState([]);
  const configuredRef = useRef(false);
  // Backend without the door_locks app answers 404: stop asking.
  const unsupportedRef = useRef(false);

  const loadStatus = useCallback(async () => {
    if (unsupportedRef.current || !apiClient.getToken()) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    try {
      const data = await doorLockService.getStatus();
      setStatus(data);
      configuredRef.current = !!data?.configured;
    } catch (error) {
      // Not logged in, no permission or backend without door locks: hide UI.
      if (error?.status === 404) unsupportedRef.current = true;
      setStatus(null);
      configuredRef.current = false;
    }
  }, []);

  const loadAttention = useCallback(async () => {
    if (!configuredRef.current) {
      setAttentionJobs([]);
      return;
    }
    if (typeof document !== 'undefined' && document.hidden) return;
    try {
      const jobs = await doorLockService.getAttentionJobs();
      setAttentionJobs(Array.isArray(jobs) ? jobs : []);
    } catch (error) {
      // Keep the last list on transient errors.
    }
  }, []);

  const refresh = useCallback(async () => {
    await loadStatus();
    await loadAttention();
  }, [loadStatus, loadAttention]);

  useEffect(() => {
    refresh();
    const statusTimer = setInterval(loadStatus, STATUS_POLL_MS);
    const attentionTimer = setInterval(loadAttention, ATTENTION_POLL_MS);
    return () => {
      clearInterval(statusTimer);
      clearInterval(attentionTimer);
    };
  }, [refresh, loadStatus, loadAttention]);

  return (
    <DoorLockContext.Provider
      value={{ status, configured: !!status?.configured, attentionJobs, refresh }}
    >
      {children}
    </DoorLockContext.Provider>
  );
}

export const useDoorLocks = () => useContext(DoorLockContext);

export default DoorLockContext;
